import { Capacitor } from '@capacitor/core';
import { CapacitorSQLite, capSQLiteSet, capSQLiteChanges, capSQLiteValues, capSQLiteResult, JsonSQLite } from '@capacitor-community/sqlite';

const DB_NAME = 'HMPos';
const DB_VERSION = 1;

class SQLiteStorage {
  private isInitialized = false;

  async init(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Check if running on native platform
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'android' || platform === 'ios';

      if (!isNative) {
        console.log('[SQLite] Running on web, using fallback storage');
        return;
      }

      // Initialize SQLite
      await CapacitorSQLite.createConnection({
        database: DB_NAME,
        version: DB_VERSION,
      });

      // Open connection
      await CapacitorSQLite.open({ database: DB_NAME });

      // Create tables
      await this.createTables();

      this.isInitialized = true;
      console.log('[SQLite] Database initialized successfully');
    } catch (error) {
      console.error('[SQLite] Initialization error:', error);
      throw error;
    }
  }

  private async createTables(): Promise<void> {
    const statements = [
      // Orders table
      `CREATE TABLE IF NOT EXISTS orders (
        _id TEXT PRIMARY KEY,
        orderNumber TEXT UNIQUE NOT NULL,
        customerName TEXT,
        customerId TEXT,
        totalAmount REAL,
        status TEXT,
        paymentStatus TEXT,
        paymentMethod TEXT,
        items TEXT,
        createdAt INTEGER,
        updatedAt INTEGER,
        synced INTEGER DEFAULT 0,
        syncStatus TEXT DEFAULT 'pending'
      )`,
      
      // Customers table
      `CREATE TABLE IF NOT EXISTS customers (
        _id TEXT PRIMARY KEY,
        firstName TEXT,
        lastName TEXT,
        email TEXT,
        phone TEXT,
        nickname TEXT,
        address TEXT,
        loyaltyPoints INTEGER DEFAULT 0,
        synced INTEGER DEFAULT 0,
        syncStatus TEXT DEFAULT 'pending'
      )`,
      
      // Services table (read-only cache)
      `CREATE TABLE IF NOT EXISTS services (
        _id TEXT PRIMARY KEY,
        name TEXT,
        category TEXT,
        price REAL,
        duration INTEGER,
        synced INTEGER DEFAULT 1
      )`,
      
      // Inventory table (read-only cache)
      `CREATE TABLE IF NOT EXISTS inventory (
        _id TEXT PRIMARY KEY,
        name TEXT,
        barcode TEXT,
        quantity INTEGER,
        minStock INTEGER,
        price REAL,
        synced INTEGER DEFAULT 1
      )`,
      
      // Sync queue table
      `CREATE TABLE IF NOT EXISTS sync_queue (
        id TEXT PRIMARY KEY,
        method TEXT,
        endpoint TEXT,
        data TEXT,
        timestamp INTEGER,
        retries INTEGER DEFAULT 0
      )`,
      
      // Create indexes for better performance
      `CREATE INDEX IF NOT EXISTS idx_orders_syncStatus ON orders(syncStatus)`,
      `CREATE INDEX IF NOT EXISTS idx_orders_createdAt ON orders(createdAt)`,
      `CREATE INDEX IF NOT EXISTS idx_customers_syncStatus ON customers(syncStatus)`,
      `CREATE INDEX IF NOT EXISTS idx_sync_queue_timestamp ON sync_queue(timestamp)`,
    ];

    for (const statement of statements) {
      await CapacitorSQLite.execute({ statements: statement });
    }
  }

  // Orders
  async saveOrder(order: any): Promise<void> {
    await this.init();
    
    const statement = `
      INSERT OR REPLACE INTO orders (
        _id, orderNumber, customerName, customerId, totalAmount, 
        status, paymentStatus, paymentMethod, items, 
        createdAt, updatedAt, synced, syncStatus
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `;

    const values: capSQLiteSet[] = [
      {
        statement,
        values: [
          order._id,
          order.orderNumber,
          order.customerName,
          order.customerId,
          order.totalAmount,
          order.status,
          order.paymentStatus,
          order.paymentMethod,
          JSON.stringify(order.items || []),
          order.createdAt ? new Date(order.createdAt).getTime() : Date.now(),
          order.updatedAt ? new Date(order.updatedAt).getTime() : Date.now(),
          0,
          'pending'
        ]
      }
    ];

    await CapacitorSQLite.executeSet({ set: values });
  }

  async getOrders(): Promise<any[]> {
    await this.init();
    
    const result = await CapacitorSQLite.query({
      statement: 'SELECT * FROM orders ORDER BY createdAt DESC',
      values: []
    });

    if (!result.values) return [];

    return result.values.map(row => ({
      ...row,
      items: JSON.parse(row.items || '[]'),
      synced: row.synced === 1,
      createdAt: new Date(row.createdAt).toISOString(),
      updatedAt: new Date(row.updatedAt).toISOString()
    }));
  }

  async getOrder(id: string): Promise<any | null> {
    await this.init();
    
    const result = await CapacitorSQLite.query({
      statement: 'SELECT * FROM orders WHERE _id = ?',
      values: [id]
    });

    if (!result.values || result.values.length === 0) return null;

    const row = result.values[0];
    return {
      ...row,
      items: JSON.parse(row.items || '[]'),
      synced: row.synced === 1,
      createdAt: new Date(row.createdAt).toISOString(),
      updatedAt: new Date(row.updatedAt).toISOString()
    };
  }

  async updateOrderSyncStatus(id: string, syncStatus: 'synced' | 'conflict'): Promise<void> {
    await this.init();
    
    await CapacitorSQLite.execute({
      statements: `UPDATE orders SET synced = 1, syncStatus = '${syncStatus}' WHERE _id = '${id}'`
    });
  }

  async getPendingOrders(): Promise<any[]> {
    await this.init();
    
    const result = await CapacitorSQLite.query({
      statement: 'SELECT * FROM orders WHERE syncStatus = ?',
      values: ['pending']
    });

    if (!result.values) return [];

    return result.values.map(row => ({
      ...row,
      items: JSON.parse(row.items || '[]'),
      synced: row.synced === 1,
      createdAt: new Date(row.createdAt).toISOString(),
      updatedAt: new Date(row.updatedAt).toISOString()
    }));
  }

  // Customers
  async saveCustomer(customer: any): Promise<void> {
    await this.init();
    
    const statement = `
      INSERT OR REPLACE INTO customers (
        _id, firstName, lastName, email, phone, nickname, 
        address, loyaltyPoints, synced, syncStatus
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `;

    const values: capSQLiteSet[] = [
      {
        statement,
        values: [
          customer._id,
          customer.firstName,
          customer.lastName,
          customer.email,
          customer.phone,
          customer.nickname,
          customer.address,
          customer.loyaltyPoints || 0,
          0,
          'pending'
        ]
      }
    ];

    await CapacitorSQLite.executeSet({ set: values });
  }

  async getCustomers(): Promise<any[]> {
    await this.init();
    
    const result = await CapacitorSQLite.query({
      statement: 'SELECT * FROM customers ORDER BY firstName, lastName',
      values: []
    });

    if (!result.values) return [];

    return result.values.map(row => ({
      ...row,
      synced: row.synced === 1
    }));
  }

  async getCustomer(id: string): Promise<any | null> {
    await this.init();
    
    const result = await CapacitorSQLite.query({
      statement: 'SELECT * FROM customers WHERE _id = ?',
      values: [id]
    });

    if (!result.values || result.values.length === 0) return null;
    return { ...result.values[0], synced: result.values[0].synced === 1 };
  }

  async updateCustomerSyncStatus(id: string, syncStatus: 'synced' | 'conflict'): Promise<void> {
    await this.init();
    
    await CapacitorSQLite.execute({
      statements: `UPDATE customers SET synced = 1, syncStatus = '${syncStatus}' WHERE _id = '${id}'`
    });
  }

  async getPendingCustomers(): Promise<any[]> {
    await this.init();
    
    const result = await CapacitorSQLite.query({
      statement: 'SELECT * FROM customers WHERE syncStatus = ?',
      values: ['pending']
    });

    if (!result.values) return [];

    return result.values.map(row => ({ ...row, synced: row.synced === 1 }));
  }

  // Services (read-only cache)
  async saveServices(services: any[]): Promise<void> {
    await this.init();
    
    const values: capSQLiteSet[] = services.map(service => ({
      statement: `INSERT OR REPLACE INTO services (_id, name, category, price, duration, synced) VALUES (?, ?, ?, ?, ?, ?)`,
      values: [
        service._id,
        service.name,
        service.category,
        service.price,
        service.duration,
        1
      ]
    }));

    await CapacitorSQLite.executeSet({ set: values });
  }

  async getServices(): Promise<any[]> {
    await this.init();
    
    const result = await CapacitorSQLite.query({
      statement: 'SELECT * FROM services',
      values: []
    });

    if (!result.values) return [];

    return result.values.map(row => ({ ...row, synced: row.synced === 1 }));
  }

  // Inventory (read-only cache)
  async saveInventory(items: any[]): Promise<void> {
    await this.init();
    
    const values: capSQLiteSet[] = items.map(item => ({
      statement: `INSERT OR REPLACE INTO inventory (_id, name, barcode, quantity, minStock, price, synced) VALUES (?, ?, ?, ?, ?, ?, ?)`,
      values: [
        item._id,
        item.name,
        item.barcode,
        item.quantity,
        item.minStock,
        item.price,
        1
      ]
    }));

    await CapacitorSQLite.executeSet({ set: values });
  }

  async getInventory(): Promise<any[]> {
    await this.init();
    
    const result = await CapacitorSQLite.query({
      statement: 'SELECT * FROM inventory',
      values: []
    });

    if (!result.values) return [];

    return result.values.map(row => ({ ...row, synced: row.synced === 1 }));
  }

  // Sync Queue
  async addToSyncQueue(item: { id: string; method: 'POST' | 'PUT' | 'DELETE'; endpoint: string; data: any }): Promise<void> {
    await this.init();
    
    const statement = `
      INSERT OR REPLACE INTO sync_queue (id, method, endpoint, data, timestamp, retries)
      VALUES (?, ?, ?, ?, ?, ?)
    `;

    const values: capSQLiteSet[] = [
      {
        statement,
        values: [
          item.id,
          item.method,
          item.endpoint,
          JSON.stringify(item.data),
          Date.now(),
          0
        ]
      }
    ];

    await CapacitorSQLite.executeSet({ set: values });
  }

  async getSyncQueue(): Promise<any[]> {
    await this.init();
    
    const result = await CapacitorSQLite.query({
      statement: 'SELECT * FROM sync_queue ORDER BY timestamp ASC',
      values: []
    });

    if (!result.values) return [];

    return result.values.map(row => ({
      ...row,
      data: JSON.parse(row.data || '{}')
    }));
  }

  async removeFromSyncQueue(id: string): Promise<void> {
    await this.init();
    
    await CapacitorSQLite.execute({
      statements: `DELETE FROM sync_queue WHERE id = '${id}'`
    });
  }

  async incrementSyncQueueRetries(id: string): Promise<void> {
    await this.init();
    
    await CapacitorSQLite.execute({
      statements: `UPDATE sync_queue SET retries = retries + 1 WHERE id = '${id}'`
    });
  }

  // Clear all data
  async clearAll(): Promise<void> {
    await this.init();
    
    const statements = [
      'DELETE FROM orders',
      'DELETE FROM customers',
      'DELETE FROM services',
      'DELETE FROM inventory',
      'DELETE FROM sync_queue'
    ];

    for (const statement of statements) {
      await CapacitorSQLite.execute({ statements: statement });
    }
  }

  // Check if running on native platform
  async isNative(): Promise<boolean> {
    const platform = Capacitor.getPlatform();
    return platform === 'android' || platform === 'ios';
  }
}

export const sqliteStorage = new SQLiteStorage();
